import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { ImageCarousel } from "../components/ImageCarousel";
import { Button } from "../components/ui/button";
import { CalendarCheck, Activity, Clock, ShieldCheck, Stethoscope, ArrowRight, CheckCircle2, Users } from "lucide-react";

const PUBLIC = process.env.PUBLIC_URL || "";
const HERO_SLIDES = [
  { src: `${PUBLIC}/carousel-1.png`, alt: "Panel de disponibilidad de citas en Veteo" },
  { src: `${PUBLIC}/carousel-2.png`, alt: "Histórico de estudios en Veteo" },
  { src: `${PUBLIC}/carousel-3.png`, alt: "Resumen de reservas y estadísticas en Veteo" },
];
const FEATURE = "/saul-eco2.jpeg";

const features = [
  { icon: CalendarCheck, title: "Agenda en tiempo real", desc: "Consulta las franjas disponibles y reserva la ecografía para tu paciente al instante, sin llamadas ni correos." },
  { icon: Activity, title: "Ecografía a domicilio", desc: "Servicio de ecografía veterinaria especializada que se desplaza a tu clínica cuando lo necesites." },
  { icon: Clock, title: "Sin esperas", desc: "Olvídate de la coordinación telefónica. Elige el hueco que mejor encaje con tu agenda clínica." },
  { icon: ShieldCheck, title: "Solo para clínicas", desc: "Plataforma dedicada. Gestiona tus reservas y el historial de estudios solicitados desde un único panel." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo-link" className="transition-opacity hover:opacity-70"><Logo className="h-10" /></Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <a href="#equipo" data-testid="nav-equipo-link" className="hidden sm:inline-flex items-center px-3 py-2 text-sm font-bold text-[#373E45] hover:text-[#6BC284] transition-colors">Equipo</a>
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="font-bold text-[#373E45]">Iniciar sesión</Button>
            </Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl">
                Registrar clínica
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 bg-[#9DC3E6]/25 text-[#373E45] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
            <Stethoscope className="h-4 w-4" /> Ecografía veterinaria · Para clínicas
          </span>
<h1 className="text-4xl sm:text-5xl lg:text-[56px] font-extrabold tracking-tight text-[#373E45] leading-[1.05]">
  Servicio de ecografía para {" "}
  <span className="text-[#7FD498]">
    tu <span className="whitespace-nowrap">clínica veterinaria</span>
  </span>
</h1>
          <p className="mt-6 text-lg text-[#64748B] leading-relaxed max-w-xl">
            Veteo conecta tu clínica con un servicio autónomo de ecografía. Consulta la disponibilidad,
            reserva la cita para tu paciente y gestiona todos tus servicios desde un panel profesional.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-register-btn">
              <Button size="lg" className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl text-base px-8 h-12">
                Empezar ahora <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-login-btn">
              <Button size="lg" variant="outline" className="rounded-xl border-slate-300 text-[#373E45] font-bold text-base px-8 h-12">
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#64748B] font-medium">
            {["Provincia de Pontevedra y alrededores", "Consulta la disponibilidad de forma inmediata", "Exclusivo para clínicas"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#7FD498]" />{t}</span>
            ))}
          </div>
        </div>
        <div className="relative fade-up">
          <div className="absolute -top-6 -left-6 w-28 h-28 bg-[#FBBC0C]/20 rounded-3xl -z-0" />
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#9DC3E6]/30 rounded-3xl -z-0" />
          <div className="relative"><ImageCarousel images={HERO_SLIDES} interval={5000} /></div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#F8FAFC] border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#373E45]">
              Pensado para el día a día de tu clínica
            </h2>
            <p className="mt-4 text-[#64748B] text-base">
              Una herramienta sencilla para solicitar ecografías sin interrumpir tu actividad.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl border border-slate-200 p-7 hover:border-[#7FD498] transition-colors" data-testid={`feature-${f.title}`}>
                <div className="h-12 w-12 rounded-xl bg-[#7FD498]/20 flex items-center justify-center mb-5">
                  <f.icon className="h-6 w-6 text-[#373E45]" />
                </div>
                <h3 className="font-bold text-[#373E45] text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <img
  src={FEATURE}
  alt="Veterinario realizando ecografía"
  className="rounded-3xl shadow-lg w-auto max-w-full max-h-[420px] h-auto border border-slate-200 order-2 lg:order-1 mx-auto"
/>
        <div className="order-1 lg:order-2">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#373E45]">Cómo funciona</h2>
          <ol className="mt-8 space-y-6">
            {[
              ["Registra tu clínica", "Crea una cuenta profesional en menos de un minuto."],
              ["Consulta la disponibilidad", "Visualiza las franjas libres del servicio de ecografía."],
              ["Reserva y describe el caso", "Indica los datos del paciente y el tipo de estudio."],
              ["Recibe la confirmación", "El veterinario confirma la cita y acude a tu clínica."],
            ].map(([t, d], i) => (
              <li key={t} className="flex gap-4">
                <span className="flex-shrink-0 h-9 w-9 rounded-full bg-[#7FD498] text-[#373E45] font-extrabold flex items-center justify-center">{i + 1}</span>
                <div>
                  <h3 className="font-bold text-[#373E45]">{t}</h3>
                  <p className="text-sm text-[#64748B] mt-1">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Equipo */}
      <section id="equipo" className="bg-[#F8FAFC] border-y border-slate-200 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <span className="inline-flex items-center gap-2 bg-[#7FD498]/20 text-[#373E45] px-4 py-1.5 rounded-full text-sm font-bold mb-5">
              <Users className="h-4 w-4" /> Equipo
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#373E45]">
              ¿Quien está detrás de Veteo?
            </h2>
            <p className="mt-4 text-[#64748B] text-base">
              Un servicio cercano y personal: yo acudo a tu clínica.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Photo */}
            <div className="relative max-w-md mx-auto lg:mx-0">
              <div className="absolute -top-5 -left-5 w-24 h-24 bg-[#FBBC0C]/20 rounded-3xl -z-0" />
              <div className="absolute -bottom-5 -right-5 w-28 h-28 bg-[#9DC3E6]/30 rounded-3xl -z-0" />
              <img
                src={`${process.env.PUBLIC_URL || ""}/saulgrelo.jpg`}
                alt="Saúl Fernández González"
                data-testid="team-photo-1"
                className="relative w-full rounded-3xl shadow-xl object-cover aspect-square border-4 border-white"
              />
            </div>

            {/* Bio */}
            <div data-testid="team-bio">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-[#373E45]">Saúl Fernández González</h3>
              <p className="mt-2 inline-flex items-center gap-2 text-[#6BC284] font-bold">
                <Stethoscope className="h-4 w-4" /> Fundador · Veterinario especialista en ecografía y cardiología
              </p>
              <div className="mt-6 space-y-4 text-[#64748B] leading-relaxed">
                <p>
                  Mi trayectoria comenzó en  <span className="font-semibold text-[#373E45]">investigación epidemiológica</span>,
                  antes de dar el salto a la práctica clínica. Tras un tiempo trabajando en un
                  <span className="font-semibold text-[#373E45]"> hospital veterinario de urgencias 24 horas en Madrid</span>,
                  regresé a Vigo.
                </p>
                <p>
                  Después de una etapa en <span className="font-semibold text-[#373E45]"> Clínica Veterinaria Menciña</span>, llevo
                  <span className="font-semibold text-[#373E45]"> tres años dirigiendo el servicio de ecografía y cardiología del Hospital Veterinario de Beade</span>,
                  donde continúo mi actividad hoy en día.
                </p>
                <p>
                  Ahora doy comienzo a <span className="font-semibold text-[#373E45]">Veteo</span> con un objetivo claro:
                  ofrecer un servicio de ecografía <span className="font-semibold text-[#373E45]">distinto y más cercano</span>,
                  desplazándome a vuestras clínicas para acompañaros en el diagnóstico de vuestros pacientes.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {["Ecografía abdominal", "Ecocardiografía", "Servicio a domicilio", "Vigo y alrededores"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-sm font-semibold text-[#373E45]">
                    <CheckCircle2 className="h-4 w-4 text-[#7FD498]" />{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#373E45] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            ¿Listo para reservar tu próxima ecografía?
          </h2>
          <p className="mt-4 text-slate-300 text-base">Únete a las clínicas que ya gestionan sus ecografías con Veteo.</p>
          <Link to="/register" data-testid="cta-register-btn">
            <Button size="lg" className="mt-8 bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl text-base px-8 h-12">
              Registrar mi clínica <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo className="h-8" />
          <p className="text-sm text-[#64748B]">© {new Date().getFullYear()} Veteo · Servicio de ecografía veterinaria</p>
        </div>
      </footer>
    </div>
  );
}
